import React, { Fragment, useEffect, useState, useMemo } from 'react';
import { Filter, DefaultColumnFilter } from './filters';
import styles from './MmhTable.module.scss';

const MmhTable = ({ columns, data, loading, renderRowSubComponent, flagForFilter, filterColumns, section = 'my-assessments' }) => {
    // State for sorting
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    
    // State for expanded rows
    const [expandedRows, setExpandedRows] = useState(new Set());
    
    // State for filters
    const [filterValues, setFilterValues] = useState({});
    
    // Apply sorting
    const sortedData = useMemo(() => {
        if (!sortConfig.key) return data;
        
        return [...data].sort((a, b) => {
            const aValue = a[sortConfig.key];
            const bValue = b[sortConfig.key];
            
            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [data, sortConfig]);

    // Apply filtering
    const filteredData = useMemo(() => {
        return sortedData.filter(row => {
            return columns.every(column => {
                const filterValue = filterValues[column.id || column.accessor];
                if (!filterValue) return true;
                
                const cellValue = row[column.id || column.accessor];
                if (cellValue === null || cellValue === undefined) return !filterValue;
                return String(cellValue).toLowerCase().includes(filterValue.toLowerCase());
            });
        });
    }, [sortedData, columns, filterValues]);

    // Handle sorting
    const handleSort = (columnId) => {
        let direction = 'asc';
        if (sortConfig.key === columnId && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key: columnId, direction });
    };

    // Toggle row expansion
    const toggleRowExpansion = (rowIndex) => {
        const newExpandedRows = new Set(expandedRows);
        if (newExpandedRows.has(rowIndex)) {
            newExpandedRows.delete(rowIndex);
        } else {
            newExpandedRows.add(rowIndex);
        }
        setExpandedRows(newExpandedRows);
    };

    // Handle filter change
    const handleFilterChange = (columnId, value) => {
        setFilterValues(prev => ({
            ...prev,
            [columnId]: value
        }));
    };

    const generateSortingIndicator = (column) => {
        if (sortConfig.key === column.id || sortConfig.key === column.accessor) {
            return sortConfig.direction === 'asc' ? ' 🔼' : ' 🔽';
        }
        return '';
    };

    const [startIndex, setStartIndex] = useState(0);
    const [endIndex, setEndIndex] = useState(0);

    useEffect(() => {
        setStartIndex(0);
        setEndIndex(5);
    }, [startIndex, endIndex])

    // Determine table class based on section
    const getTableClass = () => {
        switch (section) {
            case 'my-reports':
                return 'report-table-my-reports';
            case 'my-exercises':
                return 'report-table-my-exercises';
            case 'dashboard':
                return 'report-table-dashboard';
            case 'my-assessments':
            default:
                return 'report-assessment-table';
        }
    };

    return (
        <div className={`${styles['mmh-table__wrapper']} ${styles['mt-3']}`}>
            <div className={styles['mmh-table__container']}>
                <div className={styles['width-full']}>
                    {loading ? <div className={`${styles['mmh-table__loader']} ${styles['mb-0']} ${styles['mt-10']}`} role="alert">
                        <div className={styles['mmh-table__loader-icon']}>
                            <i className="fas fa-exclamation-triangle"/>
                        </div>
                        <div className={styles['mmh-table__loader-text']}>
                            Please wait, loading data &nbsp;&nbsp;
                            <i className="fa fa-spinner fa-spin"/>
                        </div>

                    </div> :
                        <table
                            className={`${styles['width-full']} ${getTableClass()} global-text-color`}>
                            <thead className={styles['mmh-table__head']}>
                            <tr className={`${styles['mmh-table__row']} ${styles['report-assessment-table-header']}`}
                                style={{ left: "0px" }}>
                                {columns.map((column) => (
                                    <th className={`${styles['mmh-table__cell']} ${styles['mmh-table__cell--sort']} ${styles['report-assessment-table-header__cell']} ${styles['bg-color-ice-blue']} ${styles['font-bolder']} ${styles['font-size-md']}`}
                                        style={{ verticalAlign: 'middle' }}>

                                        <div>
                                            <div className={styles['flex-center-justify']}>
                                                {typeof column.Header === 'function' ? column.Header() : column.Header}
                                            </div>

                                            {column?.HeaderSecLine && (
                                                <div className={styles['flex-center-justify']}>
                                                    {typeof column.HeaderSecLine === 'function' ? column.HeaderSecLine() : column.HeaderSecLine}
                                                </div>
                                            )}
                                        </div>

                                        {generateSortingIndicator(column)}

                                        {!flagForFilter && (
                                            <div style={{ marginTop: 5 }}>
                                                {column.canFilter && (
                                                    <input
                                                        className={styles['mmh-table__filter-input']}
                                                        value={filterValues[column.id || column.accessor] || ''}
                                                        onChange={(e) => handleFilterChange(column.id || column.accessor, e.target.value)}
                                                        placeholder={`search (${data.length}) ...`}
                                                    />
                                                )}
                                            </div>
                                        )}

                                        {flagForFilter && filterColumns?.length > 0 && (
                                            filterColumns.map((item, index) => {
                                                return (
                                                    <Fragment key={index}>
                                                        {column.id === item && (
                                                            <div style={{ marginTop: 5 }}>
                                                                {column.canFilter && (
                                                                    <input
                                                                        className={styles['mmh-table__filter-input']}
                                                                        value={filterValues[column.id || column.accessor] || ''}
                                                                        onChange={(e) => handleFilterChange(column.id || column.accessor, e.target.value)}
                                                                        placeholder={`search (${data.length}) ...`}
                                                                    />
                                                                )}
                                                            </div>
                                                        )}
                                                    </Fragment>
                                                )
                                            })
                                        )}
                                    </th>
                                ))}
                            </tr>
                            </thead>

                            <tbody className={styles['mmh-table__body']}>

                            {/* Table Rows */}
                            {filteredData.map((row, rowIndex) => {
                                return (
                                    <Fragment key={rowIndex}>
                                        <tr className={`${styles['mmh-table__row']} ${styles['font-bold']}`} style={{ left: "0px" }}
                                              onClick={renderRowSubComponent ? () => toggleRowExpansion(rowIndex) : undefined}>
                                            {columns.map((column) => {
                                                // Get the cell value - handle both direct access and accessor functions
                                                let cellValue;
                                                if (column.accessor) {
                                                    if (typeof column.accessor === 'function') {
                                                        cellValue = column.accessor(row, rowIndex);
                                                    } else {
                                                        cellValue = row[column.accessor];
                                                    }
                                                } else {
                                                    cellValue = row[column.id];
                                                }
                                                
                                                // Render the cell - handle both direct values and render functions
                                                let renderedCell;
                                                if (column.Cell) {
                                                    if (typeof column.Cell === 'function') {
                                                        renderedCell = column.Cell({ 
                                                            value: cellValue, 
                                                            row: { original: row, index: rowIndex }, 
                                                            column: column 
                                                        });
                                                    } else {
                                                        renderedCell = cellValue;
                                                    }
                                                } else {
                                                    renderedCell = typeof cellValue === 'function' ? cellValue() : cellValue;
                                                }
                                                
                                                return (
                                                    <td className={`${styles['mmh-table__cell']} table-cell`}>
                                                        <div className={column.style ? column.style : styles['width-full']}>
                                                            <div className={styles['flex-center']}>
                                                                {renderedCell}
                                                            </div>
                                                        </div>
                                                    </td>
                                                );
                                            })}
                                        </tr>

                                        {expandedRows.has(rowIndex) && renderRowSubComponent && (
                                            <tr className={styles['mmh-table__row']} style={{ left: "0px" }}>
                                                <td className={styles['mmh-table__cell']}
                                                    colSpan={columns.length}>
                                                    <span style={{ width: "118px" }}>
                                                        {renderRowSubComponent({ original: row, index: rowIndex })}
                                                    </span>
                                                </td>
                                            </tr>
                                        )}

                                    </Fragment>
                                );
                            })}

                            </tbody>
                        </table>}
                </div>
            </div>

        </div>
    );
};

export default MmhTable;
